/**
 * Direct United links for the RSW <-> EWR pair.
 *
 * The live schedule needs a FlightAware AeroAPI key, which is currently not
 * set on the Worker, so /api/flights/schedule 502s. That is a configuration
 * problem the app cannot solve for itself — but the original request was
 * "quick access to United flights EWR to RSW or RSW to EWR when clicking
 * into any day", and booking and status pages are just deep links. Those
 * work with no API, no key and no quota.
 *
 * So this renders instead of a bare error: the useful 90% of the ask,
 * available today, with an honest note about what is missing.
 */

interface Props {
  /** ISO date (YYYY-MM-DD) the user has selected. */
  date: string;
}

function unitedBookingUrl(from: string, to: string, date: string): string {
  const p = new URLSearchParams({
    f: from,
    t: to,
    d: date,
    tt: '1', // one way
    px: '1',
    taxng: '1',
    newHP: 'True',
    clm: '7',
    st: 'bestmatches',
  });
  return `https://www.united.com/en/us/fsr/choose-flights?${p.toString()}`;
}

export function UnitedQuickLinks({ date }: Props) {
  const pairs = [
    { from: 'RSW', to: 'EWR', label: 'Fort Myers → Newark' },
    { from: 'EWR', to: 'RSW', label: 'Newark → Fort Myers' },
  ];

  return (
    <div className="card">
      <h2 style={{ fontSize: 16, marginBottom: 4 }}>United, RSW &amp; EWR</h2>
      <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', lineHeight: 1.5 }}>
        Live schedules are off right now — the flight data key is not configured on the server.
        These open United directly for the date you picked.
      </p>

      {pairs.map((p) => (
        <div key={`${p.from}-${p.to}`} style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{p.label}</div>
          <div className="action-row" style={{ marginTop: 6 }}>
            <a
              className="action-btn"
              href={unitedBookingUrl(p.from, p.to, date)}
              target="_blank"
              rel="noreferrer"
            >
              Book
            </a>
            <a
              className="action-btn"
              href={`https://www.united.com/en/us/flightstatus/results?originAirport=${p.from}&destinationAirport=${p.to}&departureDate=${date}`}
              target="_blank"
              rel="noreferrer"
            >
              Status
            </a>
          </div>
        </div>
      ))}

      <p style={{ fontSize: 12, color: 'var(--color-ink-faint)', marginTop: 14, lineHeight: 1.5 }}>
        To turn live schedules back on, set the AeroAPI key on the Worker:
        <br />
        <code style={{ fontSize: 11 }}>wrangler secret put FLIGHTAWARE_API_KEY</code>
      </p>
    </div>
  );
}
